import { Icon, Input, Spinner, Text } from "@ttc3k/trekker";
import { ArrowRight, PlusCircle, Undo } from "iconoir-react";
import { useTranslation } from "react-i18next";
import { ErrorText, SelectItemCard, WhereaboutsModal } from "shared/components";
import {
  getAddressString,
  getMultilingualString,
  obfuscateEmail,
} from "shared/utils";
import { Box, Center, Flex } from "styled-system/jsx";
import { useAddBusinessListing } from "./useAddBusinessListing";
import { AddOperatorModal } from "apps/directory/Operator/components";
import { useAppContext } from "AppContext";
import { SendOneTimeCodeModal } from "../SendOneTimeCodeModal";

export type AddBusinessListingModalProps = {
  isOpen: boolean;
  onClose: () => void;
  connectedOrganizationIds?: string[];
};

export const AddBusinessListingModal = ({
  isOpen,
  onClose,
  connectedOrganizationIds,
}: AddBusinessListingModalProps) => {
  const { t } = useTranslation("core", {
    keyPrefix: "DOORSTOP.OPERATORS.ADD_MODAL",
  });

  const {
    error,
    getButtonCopy,
    getOnClickHandler,
    handleClose,
    handleKeyDown,
    handleSetSelectedOperatorEntityId,
    isCreatingNewOperator,
    isNoMatchesFound,
    isRequestingToJoin,
    isSearchDirty,
    loadingUserRequesetAccess,
    matchingOperators,
    name,
    operatorsLoading,
    searchedName,
    selectedOperator,
    selectedOperatorEntityId,
    setName,
    userData,
    userLoading,
    isModalOpen,
    setIsModalOpen,
    selectedOperatorDataForCodeModal,
  } = useAddBusinessListing(onClose);

  const { dbUser } = useAppContext();

  return (
    <>
      <WhereaboutsModal
        isOpen={isOpen}
        onClose={handleClose}
        title={t("TITLE")}
        description={t("DESCRIPTION")}
        HeaderIcon={PlusCircle}
        cancelButtonProps={{ display: "none" }}
        saveButtonCopy={getButtonCopy()}
        saveButtonProps={{
          visual: "accent.blue",
          isLoading: loadingUserRequesetAccess,
          disabled: name.length < 3 || operatorsLoading || userLoading,
          onClick: getOnClickHandler,
          Icon: isCreatingNewOperator ? ArrowRight : undefined,
          isIconRightAligned: true,
        }}
      >
        <Flex flexDir={"column"} gap={"200"}>
          <Text color={"text.mid"}>{t("INSTRUCTIONS")}</Text>
          <Box position={"relative"}>
            <Input
              label={t("LABEL")}
              placeholder={t("PLACEHOLDER") + "..."}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            {isSearchDirty && (
              <Flex position={"absolute"} top={"38px"} right={"150"} gap={"50"}>
                <Text visual={"smallTag"} color={"text.light"}>
                  {t("ENTER")}
                </Text>
                <Icon
                  Element={Undo}
                  size={"sm"}
                  color={"icon.mid"}
                  strokeWidth={1.75}
                  transform={"rotateX(180deg)"}
                />
              </Flex>
            )}
          </Box>
          {isNoMatchesFound && (
            <Text visual={"smallMedium"} color={"text.warning.mid"}>
              {t("EMPTY")}
            </Text>
          )}
          {error && <ErrorText>{t("ERROR")}</ErrorText>}
          {searchedName.length > 0 && matchingOperators.length > 0 && (
            <Text visual={"smallMedium"} color={"text.mid"}>
              {t("FOUND")}
            </Text>
          )}
          {searchedName.length > 0 &&
            matchingOperators.map((op) => (
              <SelectItemCard
                key={op._entityId}
                mainText={getMultilingualString(op.name)}
                subText={
                  op.contact.address
                    ? getAddressString(op.contact.address)
                    : undefined
                }
                onClick={() => handleSetSelectedOperatorEntityId(op._entityId)}
                selected={selectedOperatorEntityId === op._entityId}
              />
            ))}
          {userLoading ? (
            <Center>
              <Spinner width={"40px"} height={"40px"} />
            </Center>
          ) : (
            <>
              {selectedOperator && (
                <Text visual={"smallMedium"} color={"text.accent.purple.mid"}>
                  {t(
                    `SELECTED_OP.${isRequestingToJoin ? "CLAIMED" : "UNCLAIMED"}`,
                    {
                      email: isRequestingToJoin
                        ? obfuscateEmail(
                            userData?.userFindOne?.email ??
                              selectedOperator.contact.email ??
                              "",
                          )
                        : obfuscateEmail(selectedOperator.contact.email ?? ""),
                    },
                  )}
                </Text>
              )}
            </>
          )}
        </Flex>
      </WhereaboutsModal>
      <AddOperatorModal
        isOpen={isModalOpen.addNewListing}
        onClose={() =>
          setIsModalOpen((prev) => {
            return { ...prev, addNewListing: false };
          })
        }
        user={dbUser}
        organizationIds={connectedOrganizationIds}
        skipInvite
      />
      <SendOneTimeCodeModal
        isOpen={isModalOpen.sendOneTimeCode}
        onClose={() =>
          setIsModalOpen((prev) => ({ ...prev, sendOneTimeCode: false }))
        }
        operatorName={selectedOperatorDataForCodeModal.name}
        operatorEntityId={selectedOperatorDataForCodeModal._entityId}
        operatorEmail={selectedOperatorDataForCodeModal.email}
      />
    </>
  );
};
